import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";

import CloseIcon from "../assets/icons/Close Icon.png";
import OpenIcon from "../assets/icons/Open Icon.png";

const STATUSPAGE_URL = "https://outfall.statuspage.io";

export default function Status() {
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [componentsOpen, setComponentsOpen] = useState(true);

  const isStatusSubdomain =
    typeof window !== "undefined" &&
    ["status.outfall.co.uk", "status.outfall.website"].includes(
      window.location.hostname,
    );

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      const [summaryRes, incidentsRes] = await Promise.all([
        fetch(`${STATUSPAGE_URL}/api/v2/summary.json`),
        fetch(`${STATUSPAGE_URL}/api/v2/incidents.json`),
      ]);

      const summaryData = await summaryRes.json();
      const incidentsData = await incidentsRes.json();

      setSummary(summaryData);
      setIncidents(incidentsData.incidents || []);
    } catch (err) {
      console.error("Failed to load Statuspage:", err);
    }

    setLoading(false);
  }

  function formatStatus(status) {
    return status?.replaceAll("_", " ") || "unknown";
  }

  function getStatusColour(status) {
    if (status === "operational" || status === "none") return "#00875a";
    if (status === "degraded_performance" || status === "minor")
      return "#ffab00";
    if (status === "partial_outage") return "#ff991f";
    if (status === "major_outage" || status === "major") return "#de350b";
    if (status === "critical") return "#bf2600";
    if (status === "under_maintenance" || status === "maintenance")
      return "#6554c0";
    return "#42526e";
  }

  function getIncidentUrl(id) {
    return isStatusSubdomain ? `/incidents/${id}` : `/status/incidents/${id}`;
  }

  const groupedIncidents = useMemo(() => {
    const today = new Date();

    return Array.from({ length: 30 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      return {
        date,
        incidents: incidents.filter((incident) => {
          const incidentDate = new Date(incident.created_at);
          return incidentDate.toDateString() === date.toDateString();
        }),
      };
    });
  }, [incidents]);

  const components = (summary?.components || []).filter(
    (component) => component.name.toLowerCase() !== "outfall",
  );

  const pageStatus = summary?.status?.description || "Status unavailable";
  const indicator = summary?.status?.indicator || "unknown";

  if (loading) {
    return (
      <main style={styles.page}>
        <Header />
        <div style={styles.container}>
          <p style={styles.loading}>Loading status...</p>
        </div>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <Header />

      <div style={styles.container}>
        <section
          style={{
            ...styles.banner,
            background: getStatusColour(indicator),
          }}
        >
          {pageStatus}
        </section>

        <section style={styles.componentBox}>
          <button
            type="button"
            onClick={() => setComponentsOpen((prev) => !prev)}
            style={styles.groupButton}
          >
            <img
              src={componentsOpen ? CloseIcon : OpenIcon}
              alt={componentsOpen ? "Close components" : "Open components"}
              style={styles.squareIcon}
            />
            <span>Outfall</span>
          </button>

          {componentsOpen && (
            <div style={styles.componentList}>
              {components.map((component) => (
                <div key={component.id} style={styles.componentRow}>
                  <span style={styles.componentName}>{component.name}</span>

                  <span
                    style={{
                      ...styles.componentStatus,
                      color: getStatusColour(component.status),
                    }}
                  >
                    {formatStatus(component.status)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <h1 style={styles.historyTitle}>Past Incidents</h1>

          {groupedIncidents.map((day) => (
            <div key={day.date.toDateString()} style={styles.dayBlock}>
              <h2 style={styles.dateTitle}>
                {day.date.toLocaleDateString("en-GB", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h2>

              <div style={styles.divider} />

              {day.incidents.length === 0 ? (
                <p style={styles.noIncidents}>No incidents reported.</p>
              ) : (
                day.incidents.map((incident) => (
                  <article key={incident.id} style={styles.incident}>
                    <h3 style={styles.incidentTitle}>
                      <a
                        href={getIncidentUrl(incident.id)}
                        style={{
                          ...styles.incidentLink,
                          color: getStatusColour(incident.impact),
                        }}
                      >
                        {incident.name}
                      </a>
                    </h3>

                    <p style={styles.incidentMeta}>
                      {formatStatus(incident.status)} ·{" "}
                      {new Date(incident.created_at).toLocaleTimeString(
                        "en-GB",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </p>

                    {incident.incident_updates?.slice(0, 1).map((update) => (
                      <p key={update.id} style={styles.incidentPreview}>
                        {update.body}
                      </p>
                    ))}
                  </article>
                ))
              )}
            </div>
          ))}
        </section>
      </div>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#ffffff",
    color: "#172b4d",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Helvetica Neue", Arial, sans-serif',
  },

  container: {
    width: "100%",
    maxWidth: "850px",
    margin: "0 auto",
    padding: "112px 16px 60px",
    boxSizing: "border-box",
  },

  loading: {
    fontSize: "16px",
    color: "#42526e",
  },

  banner: {
    width: "100%",
    borderRadius: "4px",
    color: "#ffffff",
    padding: "18px 21px",
    fontSize: "20px",
    fontWeight: 500,
    marginBottom: "48px",
    boxSizing: "border-box",
  },

  componentBox: {
    border: "1px solid #dfe1e6",
    borderRadius: "4px",
    background: "#ffffff",
    padding: "22px 18px",
    marginBottom: "54px",
    boxSizing: "border-box",
  },

  groupButton: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    border: "none",
    background: "transparent",
    padding: 0,
    margin: "0 0 20px",
    fontSize: "16px",
    color: "#172b4d",
    cursor: "pointer",
    fontFamily: "inherit",
  },

  squareIcon: {
    width: "14px",
    height: "14px",
    objectFit: "contain",
    flexShrink: 0,
  },

  componentList: {
    display: "grid",
    gap: "22px",
    paddingLeft: "21px",
  },

  componentRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    flexWrap: "wrap",
  },

  componentName: {
    fontSize: "16px",
    color: "#172b4d",
  },

  componentStatus: {
    fontSize: "14px",
    textTransform: "capitalize",
  },

  historyTitle: {
    fontSize: "28px",
    lineHeight: "34px",
    fontWeight: 400,
    margin: "0 0 25px",
    color: "#172b4d",
  },

  dayBlock: {
    marginBottom: "36px",
  },

  dateTitle: {
    fontSize: "22px",
    lineHeight: "28px",
    fontWeight: 400,
    margin: "0 0 7px",
    color: "#172b4d",
  },

  divider: {
    height: "1px",
    background: "#dfe1e6",
    marginBottom: "12px",
  },

  noIncidents: {
    margin: 0,
    fontSize: "16px",
    color: "#5e6c84",
  },

  incident: {
    marginBottom: "18px",
  },

  incidentTitle: {
    margin: "0 0 4px",
    fontSize: "16px",
    fontWeight: 600,
  },

  incidentLink: {
    textDecoration: "none",
  },

  incidentMeta: {
    margin: "0 0 8px",
    fontSize: "13px",
    color: "#6b778c",
    textTransform: "capitalize",
  },

  incidentPreview: {
    margin: 0,
    fontSize: "15px",
    lineHeight: "22px",
    color: "#42526e",
    wordBreak: "break-word",
  },
};
